/**
 * HITL action buttons (PRD §5.5 U-05).
 *
 *   • CONFIRM   — primary, green
 *   • DISMISS   — secondary, opens reason modal
 *   • ESCALATE  — tertiary
 *
 * Optimistic updates via TanStack Query: the button is disabled the moment
 * it's clicked (prevents double-submit), the local cache is patched
 * immediately, and we invalidate on settle so the server state is the
 * source of truth.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

export interface HitlActionBarProps {
  incidentId: string;
  status: string;
  onAfterAction?: (newStatus: string) => void;
}

type Reason =
  | "VEHICLE_VIBRATION"
  | "MAINTENANCE_ACTIVITY"
  | "ENVIRONMENTAL_NOISE"
  | "DUPLICATE"
  | "OTHER";

const REASONS: Array<{ value: Reason; label: string }> = [
  { value: "VEHICLE_VIBRATION", label: "Vehicle vibration" },
  { value: "MAINTENANCE_ACTIVITY", label: "Maintenance activity" },
  { value: "ENVIRONMENTAL_NOISE", label: "Environmental noise" },
  { value: "DUPLICATE", label: "Duplicate" },
  { value: "OTHER", label: "Other (notes required)" },
];

export function HitlActionBar({
  incidentId,
  status,
  onAfterAction,
}: HitlActionBarProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showDismiss, setShowDismiss] = useState(false);
  const [reason, setReason] = useState<Reason>("VEHICLE_VIBRATION");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const confirmMut = useMutation({
    mutationFn: () =>
      api(`/incidents/${incidentId}/confirm`, {
        method: "PATCH",
        body: JSON.stringify({}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["incident", incidentId] });
      qc.invalidateQueries({ queryKey: ["incidents"] });
      onAfterAction?.("ACTIVE");
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : "confirm failed"),
  });

  const dismissMut = useMutation({
    mutationFn: () =>
      api(`/incidents/${incidentId}/dismiss`, {
        method: "PATCH",
        body: JSON.stringify({
          dismiss_reason: reason,
          dismiss_notes: notes || undefined,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["incident", incidentId] });
      qc.invalidateQueries({ queryKey: ["incidents"] });
      setShowDismiss(false);
      onAfterAction?.("RESOLVED");
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : "dismiss failed"),
  });

  const escalateMut = useMutation({
    mutationFn: () =>
      api(`/incidents/${incidentId}/escalate`, {
        method: "PATCH",
        body: JSON.stringify({
          escalation_reason: "Manual escalation by operator",
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["incident", incidentId] });
      qc.invalidateQueries({ queryKey: ["incidents"] });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : "escalate failed"),
  });

  const isTerminal = status === "RESOLVED";
  const canConfirm = status === "DRAFT";

  return (
    <div className="space-y-3" data-testid="hitl-action-bar">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="hitl-confirm"
          disabled={!canConfirm || confirmMut.isPending}
          onClick={() => confirmMut.mutate()}
          className="px-4 py-2 rounded-md bg-emerald-500 text-slate-900 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {confirmMut.isPending ? "…" : t("incident.confirm")}
        </button>
        <button
          type="button"
          data-testid="hitl-dismiss"
          disabled={isTerminal || dismissMut.isPending}
          onClick={() => setShowDismiss(true)}
          className="px-4 py-2 rounded-md bg-slate-800 text-slate-200 border border-slate-700 disabled:opacity-40"
        >
          {t("incident.dismiss")}
        </button>
        <button
          type="button"
          data-testid="hitl-escalate"
          disabled={isTerminal || escalateMut.isPending}
          onClick={() => escalateMut.mutate()}
          className="px-4 py-2 rounded-md bg-slate-900 text-amber-300 border border-amber-700 disabled:opacity-40"
        >
          {t("incident.escalate")}
        </button>
      </div>

      {error && (
        <div className="text-sm text-rose-400" data-testid="hitl-error">
          {error}
        </div>
      )}

      {showDismiss && (
        <div
          className="rounded-md border border-slate-700 bg-slate-900 p-3 space-y-2"
          data-testid="dismiss-modal"
        >
          <label className="text-xs text-slate-400">{t("dismissModal.reason")}</label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value as Reason)}
            data-testid="dismiss-reason"
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm"
          >
            {REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {t(`dismissModal.reasons.${r.value}`)}
              </option>
            ))}
          </select>
          <label className="text-xs text-slate-400">
            {t("dismissModal.notes")}{" "}
            {reason === "OTHER" && (
              <span className="text-rose-400">{t("dismissModal.notesRequired")}</span>
            )}
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="dismiss-notes"
            className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-sm"
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setShowDismiss(false)}
              data-testid="dismiss-cancel"
              className="text-xs text-slate-400 px-3 py-1"
            >
              {t("dismissModal.cancel")}
            </button>
            <button
              type="button"
              data-testid="dismiss-submit"
              disabled={
                dismissMut.isPending ||
                (reason === "OTHER" && notes.trim().length < 10)
              }
              onClick={() => dismissMut.mutate()}
              className="text-xs bg-rose-500 text-white px-3 py-1 rounded disabled:opacity-40"
            >
              {dismissMut.isPending ? "…" : t("dismissModal.submit")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
